import {
  clearProjectWorkflowArtifact,
  closeWorkflowArtifactAtom,
} from '@heidi/agents/data';
import { toast } from '@heidi/design-system/toast';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

export interface OpenRunChatOptions {
  /**
   * Land on the chat with the routine configuration closed. Set by surfaces
   * that already sit in a run chat (the Recent runs card), where a persisted
   * configuration pane restoring over the run the clinician just clicked reads
   * as the navigation not having worked. Off by default — the Routines-page
   * rails keep their existing behaviour.
   */
  chatFirst?: boolean;
}

/**
 * Open an existing routine run in the standard project chat page
 * (`/scribe/chat/projects/[id]`). A run IS a chat, so every run row — the
 * overview's Previous runs rail, the History list, the run chat's Recent runs
 * card — leaves for the chat it belongs to, where the sidebar highlights it.
 *
 * Shell-less runs (a run row with no Work shell / project id) have no chat to
 * open; those are called out rather than reading as a dead click.
 */
export function useOpenRunChat(): (
  projectId: string | null | undefined,
  options?: OpenRunChatOptions
) => Promise<boolean | void> {
  const intl = useIntl();
  const router = useRouter();
  const closeWorkflowArtifact = useSetAtom(closeWorkflowArtifactAtom);

  return useCallback(
    async (projectId, options) => {
      if (projectId) {
        if (options?.chatFirst) {
          clearProjectWorkflowArtifact(projectId);
          closeWorkflowArtifact();
        }
        return router.push(`/scribe/chat/projects/${encodeURIComponent(projectId)}`);
      }
      toast({
        title: intl.formatMessage({
          defaultMessage: 'This run has no chat to open',
          id: 'gUKMI5hmzD',
          description: 'Toast when a routine run row has no chat shell behind it',
        }),
      });
    },
    [intl, router, closeWorkflowArtifact]
  );
}
